import { Refresh as RefreshIcon, Build as BuildIcon } from '@mui/icons-material'
import { Box, Typography, Button, Alert } from '@mui/material'
import { Component, ReactNode, ErrorInfo } from 'react'

import ErrorHandler from '../../services/ErrorHandler'

interface DeskManagementErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

interface DeskManagementErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Error boundary component specifically for desk management feature
 * Handles desk operation errors gracefully with appropriate error feedback
 *
 * Requirements: 7.1, 7.3
 */
class DeskManagementErrorBoundary extends Component<
  DeskManagementErrorBoundaryProps,
  DeskManagementErrorBoundaryState
> {
  constructor(props: DeskManagementErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(
    error: Error
  ): DeskManagementErrorBoundaryState {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error using our centralized error handler
    const appError = ErrorHandler.createError(
      'system',
      `Desk management error: ${error.message}`,
      { error, errorInfo }
    )
    ErrorHandler.handleError(appError)

    this.setState({
      error,
      errorInfo
    })
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default desk management error UI
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            p: 3,
            textAlign: 'center'
          }}
        >
          <Alert severity='error' sx={{ mb: 2, width: '100%', maxWidth: 500 }}>
            <Typography variant='h6' gutterBottom>
              Desk Management Error
            </Typography>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              We encountered an issue while managing your desks. This could be
              due to a network problem, permission issue, or temporary server
              error.
            </Typography>
          </Alert>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}
          >
            <Button
              variant='contained'
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
            >
              Try Again
            </Button>

            <Button
              variant='outlined'
              startIcon={<BuildIcon />}
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography
                variant='caption'
                component='pre'
                sx={{ fontSize: '0.75rem' }}
              >
                {this.state.error.message}
                {this.state.errorInfo?.componentStack}
              </Typography>
            </Box>
          )}
        </Box>
      )
    }

    return this.props.children
  }
}

export default DeskManagementErrorBoundary
